// eslint-disable-next-line no-restricted-imports -- pre-existing in this file, inherited by the merge gate (it judges whole files); changing it alters behaviour outside this merge
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { useFloorManagement } from '@/components/portal/FloorManagement';
import { UnitsTable } from '@/components/entities/tables/units/UnitsTable';
import { EntityPageTabs } from '@/components/portal/EntityPageTabs';
import { Floor3DView } from '@/components/portal/Floor3DView';
import { FloorMapperView } from '@/components/portal/FloorMapperView';
import { EntityView } from '../EntityView';
import { toast } from 'sonner';
import type {
  FloorInventoryEntityState,
  FloorRuntimeState,
  LeasingFloorDTO,
  LeasingUnitDTO,
} from '#types/types';

type FloorWorkspaceTab = 'FOUNDATION' | 'UNITS' | 'THREE_D' | 'MAPPER';

interface FloorPageProps {
  floorId: string;
  className?: string;
  hasEntityRowAbove?: boolean;
}

const translations = {
  en: {
    tabFoundation: 'Foundation',
    tabUnits: 'Units',
    tab3d: '3D',
    tabPlan: 'Plan',
    foundationName: 'Floor name',
    foundationLevel: 'Level',
    foundationSurface: 'Surface',
    foundationDeclaredUnits: 'Declared units',
    foundationPhoto: 'Floor photo',
    foundationNoPhoto: 'No plan uploaded yet.',
    foundationReadOnly: 'Read-only',
    floorUpdated: 'Floor updated',
    floorUpdateFailed: 'Failed to update floor',
    noFloor: 'Floor not found.',
    uploadPlan: 'Upload a plan first to access floor visual tools.',
    noPlan: 'No plan attached yet.',
    upload: 'Upload',
    mapper: 'Mapper',
    calibrate: 'Calibrate',
    edit: 'Edit',
    level: (value: number) => `Level ${value}`,
    units: (count: number) => `${count} units`,
    missingArea: (count: number) => `${count} missing area`,
    missingPosition: (count: number) => `${count} missing position`,
    unitIdentifierPrompt: 'Unit identifier:',
    unitCreated: 'Unit created',
    unitCreateFailed: 'Failed to create unit',
    newUnit: 'New unit',
    loading: 'Loading floor...',
  },
  fr: {
    tabFoundation: 'Fondation',
    tabUnits: 'Unites',
    tab3d: '3D',
    tabPlan: 'Plan',
    foundationName: 'Nom etage',
    foundationLevel: 'Niveau',
    foundationSurface: 'Surface',
    foundationDeclaredUnits: 'Unites declarees',
    foundationPhoto: 'Photo etage',
    foundationNoPhoto: 'Aucun plan uploade.',
    foundationReadOnly: 'Lecture seule',
    floorUpdated: 'Etage mis a jour',
    floorUpdateFailed: 'Echec mise a jour etage',
    noFloor: 'Etage introuvable.',
    uploadPlan: 'Importez un plan pour activer les outils visuels.',
    noPlan: 'Aucun plan attache.',
    upload: 'Uploader',
    mapper: 'Mapper',
    calibrate: 'Calibrer',
    edit: 'Editer',
    level: (value: number) => `Niveau ${value}`,
    units: (count: number) => `${count} unites`,
    missingArea: (count: number) => `${count} sans surface`,
    missingPosition: (count: number) => `${count} sans position`,
    unitIdentifierPrompt: 'Identifiant unite :',
    unitCreated: 'Unite creee',
    unitCreateFailed: 'Echec creation unite',
    newUnit: 'Nouvelle unite',
    loading: 'Chargement de l etage...',
  },
};

const normalizeFloorWorkspaceTab = (value: unknown): FloorWorkspaceTab | undefined => {
  if (value === 'TWO_D') return 'MAPPER';
  if (value === 'FOUNDATION' || value === 'UNITS' || value === 'THREE_D' || value === 'MAPPER') {
    return value;
  }
  return undefined;
};

export const FloorPage = ({ floorId, className, hasEntityRowAbove = false }: FloorPageProps) => {
  const { kernel } = useKernel();
  const floorRegistry = useKernelStore(state => state.inventory.Floor.registry);
  const unitRegistry = useKernelStore(state => state.inventory.Unit.registry);
  const propertyRegistry = useKernelStore(state => state.inventory.Property.registry);
  
  const unitLastActivity = useKernelStore(state => state.inventory.Unit.lastActivity);
  const dealLastActivity = useKernelStore(state => state.inventory.Deal.lastActivity);

  const floor = useMemo(
    () => (floorRegistry[floorId] as FloorInventoryEntityState | undefined) || null,
    [floorId, floorRegistry]
  );

  const unitIds = floor?.unitIds || [];
  const unitIdsSignature = unitIds.join(',');

  // 2. EFFET A : Hydrater (ou upgrader) les Unités de cet étage
  useEffect(() => {
    const k = kernel.current;
    if (!k || unitIds.length === 0) return;

    const unitReg = k.getInventory().Unit.registry;

    // On cherche les unités qui n'existent pas OU qui n'ont pas leur geoJson pour cet étage
    const idsToHydrate = unitIds.filter(id => {
      const u = unitReg[id];
      if (!u) return true; // Unité totalement absente du cache
      
      // L'unité est là, mais a-t-elle sa géométrie pour cet étage ?
      const pos = u.positions?.find(p => p.floorId === floorId);
      if (!pos || !pos.geoJson) return true; // Il manque le geoJson, on doit forcer le fetch
      
      return false;
    });

    if (idsToHydrate.length === 0) return;

    const run = async () => {
      const batchSize = 200;
      for (let i = 0; i < idsToHydrate.length; i += batchSize) {
        const batch = idsToHydrate.slice(i, i + batchSize);
        if (batch.length === 0) continue;
        await k.queryInventory('Unit', {
          filters: { ids: batch },
          limit: batch.length,
          force: true,
        });
      }
    };

    void run();
  }, [unitIdsSignature, floorId, kernel]);

  // 3. EFFET B : Hydrater les Deals et les Marques (Cascade)
  // Indispensable pour afficher les logos/noms sur le plan 3D
  useEffect(() => {
    const k = kernel.current;
    if (!k || unitIds.length === 0) return;

    const inv = k.getInventory();
    const unitReg = inv.Unit.registry;
    const dealReg = inv.Deal.registry;
    const brandReg = inv.Brand.registry;

    const dealIdsToFetch = new Set<string>();
    const brandIdsToFetch = new Set<string>();

    unitIds.forEach(id => {
      const unit = unitReg[id];
      if (!unit) return;
      
      // Collecte des Deals manquants
      if (unit.activeDealId && !dealReg[unit.activeDealId]) {
        dealIdsToFetch.add(unit.activeDealId);
      }
      
      // Collecte des Marques manquantes (si le deal est déjà là)
      const deal = unit.activeDealId ? dealReg[unit.activeDealId] : null;
      if (deal?.brandId && !brandReg[deal.brandId]) {
        brandIdsToFetch.add(deal.brandId);
      }
    });

    if (dealIdsToFetch.size > 0) k.ensureInventoryIds('Deal', Array.from(dealIdsToFetch));
    if (brandIdsToFetch.size > 0) k.ensureInventoryIds('Brand', Array.from(brandIdsToFetch));

  }, [unitIdsSignature, unitLastActivity, dealLastActivity, kernel]);

  // 4. On dérive les unités réelles pour la vue 3D
  // On utilise unitLastActivity pour recalculer quand le registre se remplit
  const unitsOnFloor = useMemo(() => {
    return unitIds.map(id => unitRegistry[id]).filter(Boolean) as LeasingUnitDTO[];
  }, [unitIds, unitRegistry]);
  const lang = useKernelStore((state) => state.language);
  const t = translations[lang];

  const property = useMemo(() => 
    floor ? propertyRegistry[floor.propertyId] : null
  , [floor, propertyRegistry]);

  // Les outils de plan (Foundation, 3D, Mapper) sont derriere une permission. Sans elle, un
  // etage reste la vue « unites + occupants » : pas de barre d'onglets, et l'onglet effectif
  // est force sur UNITS meme si le runtime en a memorise un autre (permission retiree en
  // cours de route, ou etat restaure depuis l'URL).
  const canAccessFloorPlanTools = useKernelStore((state) =>
    Boolean(state.userContext.permissions?.canAccessFloorPlanTools)
  );

  const storedTab =
    normalizeFloorWorkspaceTab(floor?.activeTab ?? floor?.runtime?.activeTab) ?? 'FOUNDATION';
  const activeTab: FloorWorkspaceTab = canAccessFloorPlanTools ? storedTab : 'UNITS';
  const hasPlan = Boolean(floor?.planFileId);

  const {
    handleUploadPlan,
    handleFloorPlanFileChange,
    floorPlanInputRef,
  } = useFloorManagement(floor?.propertyId || property?.id || null);

  const [nameDraft, setNameDraft] = useState('');
  const [levelDraft, setLevelDraft] = useState('0');
  const [surfaceDraft, setSurfaceDraft] = useState('');
  const [unitCountDraft, setUnitCountDraft] = useState('0');

  useEffect(() => {
    if (!floor) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing in this file, inherited by the merge gate (it judges whole files); changing it alters behaviour outside this merge
    setNameDraft(floor.name || '');
    setLevelDraft(String(floor.level ?? 0));
    setSurfaceDraft(typeof floor.surface === 'number' ? String(floor.surface) : '');
    setUnitCountDraft(String(Math.max(0, floor.unitCount ?? 0)));
  }, [floor?.id, floor?.level, floor?.name, floor?.surface, floor?.unitCount]);

  const patchFloorRuntimeState = useCallback(
    (patch: Partial<Pick<FloorRuntimeState, 'activeTab' | 'mapperState'>>) => {
      if (!kernel.current) return;
      kernel.current.patchEntityRuntimeState('Floor', floorId, patch);
    },
    [floorId, kernel]
  );

  useEffect(() => {
    if (!kernel.current) return;
    if (normalizeFloorWorkspaceTab(floor?.activeTab ?? floor?.runtime?.activeTab)) return;
    kernel.current.patchEntityRuntimeState('Floor', floorId, {
      activeTab: 'FOUNDATION',
    });
  }, [kernel, floorId, floor?.activeTab, floor?.runtime?.activeTab]);

  useEffect(() => {
    if (!kernel.current || !floorId) return;

    // IMPORTANT: une ligne Floor venant d'une liste peut exister sans unitIds (mode summary).
    // On force alors le détail pour récupérer le scope complet du mapper.
    if (floor && Array.isArray(floor.unitIds)) return;

    void kernel.current.syncEntity('Floor', floorId);
  }, [kernel, floorId, floor, floor?.unitIds]);

  const persistFloorPatch = useCallback(
    async (patch: Partial<Pick<LeasingFloorDTO, 'name' | 'level' | 'surface' | 'unitCount'>>) => {
      if (!kernel.current || !floor) return;
      await kernel.current.mutateInventory(
        [{ action: 'UPDATE', entity: 'Floor', id: floor.id, payload: patch }],
        ''
      );
      toast.success(t.floorUpdated);
    },
    [floor, kernel, t.floorUpdated]
  );

  const handleFoundationNameBlur = useCallback(async () => {
    if (!floor) return;
    const normalized = (nameDraft || '').trim();
    if (!normalized) {
      setNameDraft(floor.name);
      return;
    }
    if (normalized === floor.name) return;
    try {
      await persistFloorPatch({ name: normalized });
    } catch (error) {
      console.error('[FloorPage] Failed to update floor name', error);
      setNameDraft(floor.name);
      toast.error(t.floorUpdateFailed);
    }
  }, [floor, nameDraft, persistFloorPatch, t.floorUpdateFailed]);

  const handleFoundationLevelBlur = useCallback(async () => {
    if (!floor) return;
    const parsed = Number.parseInt(levelDraft, 10);
    if (!Number.isFinite(parsed)) {
      setLevelDraft(String(floor.level));
      return;
    }
    if (parsed === floor.level) return;
    try {
      await persistFloorPatch({ level: parsed });
    } catch (error) {
      console.error('[FloorPage] Failed to update floor level', error);
      setLevelDraft(String(floor.level));
      toast.error(t.floorUpdateFailed);
    }
  }, [floor, levelDraft, persistFloorPatch, t.floorUpdateFailed]);

  const handleFoundationSurfaceBlur = useCallback(async () => {
    if (!floor) return;
    const normalized = surfaceDraft.trim();
    const parsed = normalized === '' ? null : Number.parseFloat(normalized);
    if (normalized !== '' && !Number.isFinite(parsed as number)) {
      setSurfaceDraft(typeof floor.surface === 'number' ? String(floor.surface) : '');
      return;
    }
    if (parsed === floor.surface) return;
    try {
      await persistFloorPatch({ surface: parsed });
    } catch (error) {
      console.error('[FloorPage] Failed to update floor surface', error);
      setSurfaceDraft(typeof floor.surface === 'number' ? String(floor.surface) : '');
      toast.error(t.floorUpdateFailed);
    }
  }, [floor, persistFloorPatch, surfaceDraft, t.floorUpdateFailed]);

  const handleFoundationUnitCountBlur = useCallback(async () => {
    if (!floor) return;
    const parsed = Number.parseInt(unitCountDraft, 10);
    if (!Number.isFinite(parsed) || parsed < 0) {
      setUnitCountDraft(String(Math.max(0, floor.unitCount ?? 0)));
      return;
    }
    if (parsed === floor.unitCount) return;
    try {
      await persistFloorPatch({ unitCount: parsed });
    } catch (error) {
      console.error('[FloorPage] Failed to update floor unit count', error);
      setUnitCountDraft(String(Math.max(0, floor.unitCount ?? 0)));
      toast.error(t.floorUpdateFailed);
    }
  }, [floor, persistFloorPatch, t.floorUpdateFailed, unitCountDraft]);

  const unitsStats = useMemo(() => {
    let missingArea = 0;
    let missingPosition = 0;
    for (const unit of unitsOnFloor) {
      if (!(typeof unit.areaSqm === 'number' && unit.areaSqm > 0) && !(typeof unit.areaSqft === 'number' && unit.areaSqft > 0)) {
        missingArea += 1;
      }
      const hasPosition = Array.isArray(unit.positions) && unit.positions.some((position) => Boolean(position.geoJson));
      if (!hasPosition) {
        missingPosition += 1;
      }
    }
    return { total: unitsOnFloor.length, missingArea, missingPosition };
  }, [unitsOnFloor]);

  const handleCreateUnit = useCallback(async () => {
    if (!kernel.current || !floor) return;
    const rawIdentifier = window.prompt(t.unitIdentifierPrompt);
    const unitIdentifier = rawIdentifier?.trim() || '';
    if (!unitIdentifier) return;

    try {
      await kernel.current.mutateInventory(
        [
          {
            action: 'CREATE',
            entity: 'Unit',
            payload: {
              unitIdentifier,
              propertyId: floor.propertyId,
              floorId: floor.id,
            },
          },
        ],
        ''
      );
      toast.success(t.unitCreated);
    } catch (error) {
      console.error('[FloorPage] Failed to create unit', error);
      toast.error(t.unitCreateFailed);
    }
  }, [floor, kernel, t.unitCreateFailed, t.unitCreated, t.unitIdentifierPrompt]);

  const renderUnitsToolbarHeader = useCallback(
    () => (
      <div className="flex items-center gap-2 text-[12px] text-white/70">
        <span>{property?.name || t.noFloor}</span>
        <span className="text-white/30">-</span>
        <span>{floor?.name || '-'}</span>
        <span className="text-white/30">|</span>
        <span className="text-white/50">{t.units(unitsStats.total)}</span>
        <span className="text-white/50">{t.missingArea(unitsStats.missingArea)}</span>
        <span className="text-white/50">{t.missingPosition(unitsStats.missingPosition)}</span>
      </div>
    ),
    [floor?.name, property?.name, t, unitsStats.missingArea, unitsStats.missingPosition, unitsStats.total]
  );

  const tabs = useMemo(
    () => [
      { id: 'FOUNDATION' as const, label: t.tabFoundation },
      { id: 'UNITS' as const, label: t.tabUnits },
      { id: 'THREE_D' as const, label: t.tab3d },
      { id: 'MAPPER' as const, label: t.tabPlan },
    ],
    [t]
  );

  const handleTabChange = useCallback(
    (nextTab: FloorWorkspaceTab) => patchFloorRuntimeState({ activeTab: nextTab }),
    [patchFloorRuntimeState]
  );

  const handleNameDraftChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setNameDraft(event.target.value),
    []
  );
  const handleLevelDraftChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setLevelDraft(event.target.value),
    []
  );
  const handleSurfaceDraftChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setSurfaceDraft(event.target.value),
    []
  );
  const handleUnitCountDraftChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setUnitCountDraft(event.target.value),
    []
  );

  const handleNameBlur = useCallback(() => void handleFoundationNameBlur(), [handleFoundationNameBlur]);
  const handleLevelBlur = useCallback(() => void handleFoundationLevelBlur(), [handleFoundationLevelBlur]);
  const handleSurfaceBlur = useCallback(
    () => void handleFoundationSurfaceBlur(),
    [handleFoundationSurfaceBlur]
  );
  const handleUnitCountBlur = useCallback(
    () => void handleFoundationUnitCountBlur(),
    [handleFoundationUnitCountBlur]
  );

  const handleUploadPlanClick = useCallback(() => {
    if (!floor) return;
    handleUploadPlan(floor.id);
  }, [floor, handleUploadPlan]);

  const unitsLockedFilters = useMemo(
    () => ({ propertyId: floor?.propertyId, floorId: floor?.id }),
    [floor?.id, floor?.propertyId]
  );

  if (!floor) {
    return (
      <div className={clsx('flex h-full items-center justify-center text-sm text-white/35', className)}>
        {t.loading}
      </div>
    );
  }

  // Sans `canAccessFloorPlanTools`, un etage est ses unites + leurs occupants, et rien
  // d'autre : ni barre d'onglets, ni input de plan. C'est la vue simplifiee du 18/07/2026,
  // laissee intacte. Avec la permission, les outils de plan reviennent tels qu'ils etaient.
  return (
    <div className={clsx('flex h-full min-h-0 flex-col bg-black', className)}>
      {canAccessFloorPlanTools && (
        <>
          <input
            ref={floorPlanInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFloorPlanFileChange}
          />

          {!hasEntityRowAbove && (
            <EntityView type="Floor" entityId={floorId} variant="row" className="!cursor-default" />
          )}
          <EntityPageTabs
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />
        </>
      )}

      <div className="min-h-0 flex-1 p-3">
        {activeTab === 'FOUNDATION' ? (
          <div className="grid h-full min-h-0 gap-3 lg:grid-cols-[360px_minmax(0,1fr)]">
            <div className="flex min-h-0 flex-col gap-3">
              <div className="rounded-xl border border-[#2A2A2A] bg-[#1B1B1B] p-3">
                <div className="grid grid-cols-1 gap-3">
                  <label className="flex flex-col gap-1">
                    <span className="text-[11px] text-white/55">{t.foundationName}</span>
                    <input
                      type="text"
                      value={nameDraft}
                      onChange={handleNameDraftChange}
                      onBlur={handleNameBlur}
                      className="h-9 rounded-md border border-white/10 bg-[#2A2A2A] px-2 text-[13px] text-white outline-none transition-colors focus:border-white/25"
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-[11px] text-white/55">{t.foundationLevel}</span>
                    <input
                      type="number"
                      step={1}
                      value={levelDraft}
                      onChange={handleLevelDraftChange}
                      onBlur={handleLevelBlur}
                      className="h-9 rounded-md border border-white/10 bg-[#2A2A2A] px-2 text-[13px] text-white outline-none transition-colors focus:border-white/25"
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-[11px] text-white/55">{t.foundationSurface} (m2)</span>
                    <input
                      type="number"
                      step={0.01}
                      value={surfaceDraft}
                      onChange={handleSurfaceDraftChange}
                      onBlur={handleSurfaceBlur}
                      className="h-9 rounded-md border border-white/10 bg-[#2A2A2A] px-2 text-[13px] text-white outline-none transition-colors focus:border-white/25"
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-[11px] text-white/55">{t.foundationDeclaredUnits}</span>
                    <input
                      type="number"
                      min={0}
                      step={1}
                      value={unitCountDraft}
                      onChange={handleUnitCountDraftChange}
                      onBlur={handleUnitCountBlur}
                      className="h-9 rounded-md border border-white/10 bg-[#2A2A2A] px-2 text-[13px] text-white outline-none transition-colors focus:border-white/25"
                    />
                  </label>
                </div>
              </div>
            </div>

            <div className="min-h-0 rounded-xl border border-[#2A2A2A] bg-[#1B1B1B] p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-[12px] text-white/70">{t.foundationPhoto}</span>
                <button
                  type="button"
                  onClick={handleUploadPlanClick}
                  className="rounded border border-white/15 bg-white/5 px-2 py-1 text-[10px] text-white/80 transition-colors hover:bg-white/10"
                >
                  {t.upload}
                </button>
              </div>
              {floor.planFileId ? (
                <div className="h-full min-h-[420px] overflow-hidden rounded-lg border border-white/10 bg-[#141414]">
                  <img
                    src={`/api/inventory/assets/${floor.planFileId}`}
                    alt={floor.name}
                    className="h-full w-full object-contain"
                  />
                </div>
              ) : (
                <div className="flex h-full min-h-[420px] items-center justify-center rounded-lg border border-dashed border-white/15 bg-white/[0.02] text-[12px] text-white/40">
                  {t.foundationNoPhoto}
                </div>
              )}
            </div>
          </div>
        ) : activeTab === 'UNITS' ? (
          <div className="h-full">
            <UnitsTable
              lockedFilters={unitsLockedFilters}
              assetLedgerTitle={lang === 'fr' ? 'Unites de l etage' : 'Floor units'}
              assetLedgerBreadcrumb={lang === 'fr' ? 'Etage / Unites' : 'Floor / Units'}
              showDefaultToolbarSelectors={false}
              renderCustomToolbarHeader={renderUnitsToolbarHeader}
              onCreateClick={handleCreateUnit}
              createButtonLabel={t.newUnit}
            />
          </div>
        ) : !hasPlan ? (
          <div className="flex h-full flex-col items-center justify-center rounded-xl border border-dashed border-white/15 bg-white/[0.02] px-4 text-center">
            <div className="text-sm text-white/45">{t.noPlan}</div>
            <div className="mt-1 text-xs text-white/35">{t.uploadPlan}</div>
          </div>
        ) : activeTab === 'THREE_D' ? (
          <Floor3DView floorId={floor.id} mode="cinematic3d" enforcedAspectRatio="16 / 9" />
        ) : (
          <div className="relative flex h-[75dvh] min-h-[600px] flex-col overflow-hidden rounded-xl border border-white/10">
            <FloorMapperView floorId={floor.id} />
          </div>
        )}
      </div>
    </div>
  );
};

